import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape GEO-05 publishes, imported rather than restated. A column here that the service
// stopped returning is a build failure, not a blank cell.
import type { Geofence } from "@tdelivr/api-client/generated/geo/geofence.js";

import {
  geofenceDetail,
  amendment,
  boundedNote,
  centreText,
  creation,
  draftOf,
  geofencePage,
  shapeText,
  stateOf,
  EMPTY_DRAFT,
  FILTERABLE_STATUSES,
  PER_FETCH,
  type DraftProblem,
  type GeofenceDraft,
  type GeofenceOrder,
  type GeofenceStatus,
} from "../geofences.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Geofences: the boundaries this tenant has drawn, and the form that amends one.
 *
 * **No design file draws this page.** `02 Console Shell v3.dc.html` names `Geofences` in its rail
 * and gives it the glyph `polyline`; `04 Dispatch Exceptions Board` and `05 Dispatch Live Map` both
 * carry it as a *map layer*; and no file in the bundle draws a geofence board or a geofence form.
 * The nearest designed board on this subject is `17 WP16 Tenant Admin.dc.html`, page `zones`, which
 * is a tenant-admin screen about service areas. So this page is built to that file's board and form
 * template — the same `gridUI` and `formUI` the offer monitor and the demand queue are built to,
 * value for value — with GEO-05's own columns and GEO-05's own fields in it. Recorded as Q-40 in
 * `docs/design/DESIGN_QUESTIONS.md` rather than treated as a licence to invent: the geometry is the
 * design's and the content is the contract's.
 *
 * **Three columns, three sources, and the third is the one worth reading closely.** `Fence` is
 * `name`; `Shape` is the discriminated `geometry`, said as a radius or a corner count because those
 * are the only extents published; `Centre` is a circle's own centre or, for a polygon, the mean of
 * its vertices — which is the centroid of the corners rather than of the area, and is said to be
 * that in `centreText`. There is no `Region` column because GEO-05 publishes no market, and no
 * tiles because it publishes no total (R-04).
 *
 * **The amendment form is the point of this page, and until GEO-05 published a read it could not
 * exist.** `PUT` requires an `expectedVersion`; that number was disclosed in a write response and
 * nowhere else, so the only caller who could amend a fence was whoever had last written one. Both
 * reads now publish `version`, so the form carries the version of the row the operator opened — and
 * a fence somebody else moved in the meantime is refused with GEO-05's `version_conflict` and said
 * so, which is the designed 409 state reached by a real 409 rather than a hypothetical one.
 */

const geofencesApi = services.geo__geofence;

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: GeofenceOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

/**
 * The scope every command on this page needs.
 *
 * An operator holding only `geo:geofence:read` is view-only, which the design draws as a chip beside
 * the title and every action visible but disabled. `navigation.ts` already hides the page from an
 * operator holding neither, so this is about the half of the page that writes.
 */
const WRITE_SCOPE = "geo:geofence:write";

export interface GeofencesProps {
  readonly transport: Transport;
  /** The verified tenant, which is in this service's path rather than only in the token. */
  readonly tenantId: string;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** What this operator holds. Decides the view-only chip and whether the form's actions are live. */
  readonly scopes?: ReadonlySet<string>;
  /**
   * The idempotency key for the next command.
   *
   * Injected so a test and the fidelity harness can drive a create and an amendment without a
   * random value in the request, and because `crypto.randomUUID` is not available on every
   * origin a console is served from — it requires a secure context, and a developer serving over
   * plain HTTP would get a page whose buttons throw.
   */
  readonly newKey?: () => string;
}

interface Loaded {
  readonly rows: readonly Geofence[];
  /** GEO-05 answered a cursor, so this is the head of the list rather than all of it. */
  readonly truncated: boolean;
}

/** Which fence the form is editing, or that it is making a new one. */
type FormTarget = { readonly kind: "new" } | { readonly kind: "edit"; readonly fence: Geofence };

export function Geofences({
  transport,
  tenantId,
  crumbs,
  scopes,
  newKey,
}: GeofencesProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selectedFenceId, setSelectedFenceId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<GeofenceStatus | undefined>(undefined);
  const [order, setOrder] = useState<GeofenceOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const [target, setTarget] = useState<FormTarget | undefined>(undefined);
  const [draft, setDraft] = useState<GeofenceDraft>(EMPTY_DRAFT);
  const [problems, setProblems] = useState<readonly DraftProblem[]>([]);
  const [refusal, setRefusal] = useState<ViewState | undefined>(undefined);
  const [saved, setSaved] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const client = geofencesApi.createGeofenceClient(transport);
    try {
      const answered = await client.getTenantsGeofences(tenantId, status, PER_FETCH, undefined);
      setLoaded({
        rows: answered.geofences,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      // One state rather than a banner over a half-rendered table. A board showing two of seven
      // boundaries with no explanation is worse than one showing none.
      setFailure(stateForError(error, ["geo:geofence:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an operator uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  /* Across everything fetched, so the panel survives a page turn or a narrowed search. */
  const selectedFence = loaded?.rows.find((row) => row.id === selectedFenceId);

  const view = useMemo(
    () => geofencePage(loaded?.rows ?? [], { search, order, page, perPage }),
    [loaded, search, order, page, perPage],
  );

  /*
   * No `observedAt`, so no stale state.
   *
   * GEO-05's listing publishes `updatedAt` per row — when the boundary was last changed — and no
   * page-level instant, so this screen cannot tell an operator that what they are looking at is a
   * minute old. It is the least costly place that absence has been found: a boundary is
   * configuration rather than a moving thing. Recorded in R-19 all the same, because the
   * recommendation is the same one and it is one field.
   */
  /*
   * A narrowed board stays `ready`, so the card can draw its own empty state.
   *
   * This read `stateForList(loaded?.rows, { filtered: status !== undefined })`, and that
   * stranded a control the design requires. Handed an empty list with `filtered: true`,
   * `stateForList` answers `empty/filtered` and `StateView` draws "Nothing matches this filter ·
   * Clear the filter to see everything on this page" **in place of its children** — so the
   * `search_off` block below, with the Clear filters button design rule 4 asks of every grid, was
   * never rendered. The sentence told the operator to clear a filter and gave them nothing to clear
   * it with. Reachable in one click: pick a status that answers no rows.
   *
   * The design has no such split. Its `grid.empty` block sits inside the card, above the pager,
   * whenever the row count is zero for any reason. So `stateForList` is consulted for the
   * unfiltered cases only, and its empty state is kept for the one case it is right for — a tenant
   * that genuinely has no rows, where there is no filter to clear and the sentence worth showing is
   * what would make one appear.
   *
   * Found by the agent that built the tenant admin console's five new boards, which hit the same
   * fault in the module copied from here and reported it against these screens rather than
   * changing them. The same two modules are copied into the finance console, which passes no
   * `filtered` flag at all — so its boards say "Nothing here yet" when a server-side filter is
   * what emptied them. That is a wrong sentence rather than a missing control, and is recorded.
   */
  const narrowed = status !== undefined || search.trim().length > 0;
  const viewState: ViewState =
    failure ??
    (narrowed && loaded?.rows !== undefined
      ? { kind: "ready", stale: false }
      : stateForList(loaded?.rows));
  const viewOnly = scopes !== undefined && !scopes.has(WRITE_SCOPE);

  const clearFilters = (): void => {
    setSearch("");
    setStatus(undefined);
    setPage(1);
  };

  const bounded = boundedNote(
    loaded?.rows.length,
    loaded?.truncated ?? false,
    status,
    Number(PER_FETCH),
  );

  const openForm = (next: FormTarget): void => {
    setTarget(next);
    setDraft(next.kind === "edit" ? draftOf(next.fence) : EMPTY_DRAFT);
    setProblems([]);
    setRefusal(undefined);
    setSaved(undefined);
  };

  const closeForm = (): void => {
    setTarget(undefined);
    setProblems([]);
    setRefusal(undefined);
    setSaved(undefined);
  };

  const submit = async (): Promise<void> => {
    if (target === undefined) return;
    const made = target.kind === "new" ? creation(draft) : amendment(draft, target.fence.version);
    if (Array.isArray(made)) {
      setProblems(made as readonly DraftProblem[]);
      setRefusal(undefined);
      return;
    }
    setProblems([]);
    setRefusal(undefined);
    setSaving(true);
    const client = geofencesApi.createGeofenceClient(transport);
    const idempotencyKey = newKey === undefined ? randomKey() : newKey();
    try {
      if (target.kind === "new") {
        await client.postTenantsGeofences(
          tenantId,
          made as { name: string; geometry: Geofence["geometry"] },
          { idempotencyKey },
        );
        setSaved("Saved. It now appears in the list.");
      } else {
        await client.putTenantsGeofences(
          tenantId,
          target.fence.id,
          made as Parameters<
            ReturnType<typeof geofencesApi.createGeofenceClient>["putTenantsGeofences"]
          >[2],
          { idempotencyKey },
        );
        setSaved("Saved. The boundary now in force is the one above.");
      }
      await load();
    } catch (error) {
      /*
       * The 409 this page was built for, reached rather than imagined.
       *
       * `stateForError` turns GEO-05's `conflict` category into the conflict state, which the design
       * draws as news rather than as a failure: somebody else got there first. It is shown inside
       * the form, beside the values the operator typed, because that is where the decision is — the
       * alternative is closing their work and telling them to start again.
       */
      setRefusal(stateForError(error, [WRITE_SCOPE]));
    } finally {
      setSaving(false);
    }
  };

  const problemFor = (field: keyof GeofenceDraft): string | undefined =>
    problems.find((problem) => problem.field === field)?.message;

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="fences__head">
      <span>Fence</span>
      <span>Shape</span>
      <span>Centre</span>
      <span className="fences__head--right">State</span>
    </div>
  );

  return (
    <section className="page fences">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Geofences"
        source="GEO-05"
        description="Draw and edit the geofences trips are evaluated against."
        {...(viewOnly
          ? {
              aside: (
                <span className="fences__role-note">
                  <span aria-hidden="true" className="fences__role-glyph">
                    visibility
                  </span>
                  View only · geo:geofence:read
                </span>
              ),
            }
          : {})}
        action={
          <button
            type="button"
            className="fences__primary"
            disabled={viewOnly}
            {...(viewOnly ? { title: `Drawing a geofence needs ${WRITE_SCOPE}.` } : {})}
            onClick={() => openForm({ kind: "new" })}
          >
            <span aria-hidden="true" className="fences__primary-glyph">
              add
            </span>
            Add geofence
          </button>
        }
      />

      {/*
        No tiles, where the design's board template has four.

        Every tile in `17 WP16 Tenant Admin` is a count — `Zones · 34`, `Partial · 5` — and no
        listing on this platform publishes a total (R-04), so a tile here would read "34" while
        meaning "34 so far". A count of the rows this page happens to hold under a label that means
        every fence is a number an operator would work. R-19.
      */}

      {/*
        The grid and its panel in one positioned container, which is what docks the panel.

        `.detail` is docked two ways and both are written against `.board-split`: at 1240 and
        above `.board-split:has(> .detail)` makes it a 420px second column, and below 1240 the
        panel is `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s
        own `position: relative`. This board had no such wrapper, so an opened row drew a
        full-width block stacked under the table at 1440, and below 1240 an overlay anchored to
        the page rather than to the board. `DemandQueue.tsx` carries the measurements that found
        it: the first time any of these six panels was photographed, on 1 November.

        Which docking is right for these six at all is Q-297 — `17 WP16 Tenant Admin` draws
        a centred modal where `04 Dispatch Exceptions Board` draws this column, and that is a
        decision rather than a defect. The wrapper is not that decision: it is what makes the
        build do what its own stylesheet already says.
      */}
      <div className="board-split">
        <div className="fences__panel">
          <div className="fences__toolbar">
            <span className="fences__search">
              <span aria-hidden="true" className="fences__search-glyph">
                search
              </span>
              <input
                id="geofences-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="geofences-status"
              aria-label="Filter by state"
              value={status ?? ""}
              onChange={(event) => {
                setStatus(
                  event.target.value === "" ? undefined : (event.target.value as GeofenceStatus),
                );
                setPage(1);
              }}
            >
              {/*
              The design's word for the unfiltered option, over GEO-05's own two statuses rather
              than over the states on screen. Choosing one refetches: the listing publishes a
              `status` parameter, so a filter built from the rows held would narrow the first 200
              alphabetically instead of asking the service for the switched-off ones.
            */}
              <option value="">All states</option>
              {FILTERABLE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <select
              id="geofences-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as GeofenceOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="fences__export" ref={exportRegion}>
              <button
                type="button"
                className="fences__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="fences__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="fences__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="fences__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        exportRows(view.all, choice.format);
                      }}
                    >
                      <span aria-hidden="true" className="fences__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={viewState}
            purpose="Geofences appear here as soon as one is drawn for this tenant."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "fences__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="fences__rows">
              {view.rows.map((fence) => {
                const shown = stateOf(fence);
                return (
                  <div
                    key={fence.id}
                    className="fences__row"
                    role="button"
                    tabIndex={0}
                    data-selected={fence.id === selectedFenceId ? "true" : undefined}
                    aria-label={viewOnly ? `Open geofence ${fence.id}` : `Edit ${fence.name}`}
                    onClick={
                      viewOnly
                        ? (): void => setSelectedFenceId(fence.id)
                        : (): void => openForm({ kind: "edit", fence })
                    }
                    onKeyDown={(event): void => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      if (viewOnly) setSelectedFenceId(fence.id);
                      else openForm({ kind: "edit", fence });
                    }}
                  >
                    <span className="fences__name">{fence.name}</span>
                    <span className="fences__shape">{shapeText(fence.geometry)}</span>
                    <span className="fences__centre">{centreText(fence.geometry)}</span>
                    <span className="fences__state">
                      <StateBadge
                        code="GEO-05"
                        status={fence.status}
                        classKey={shown.classKey}
                        label={shown.label}
                        title={`Version ${fence.version}, last changed ${fence.updatedAt}. An amendment carries this version, so a boundary somebody else moved first is refused rather than overwritten.`}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? (
              <div className="fences__no-match">
                <span aria-hidden="true" className="fences__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="fences__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
            <div className="fences__pager">
              <span>
                {view.total === 0
                  ? "No matching rows"
                  : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
              </span>
              <span className="fences__pager-controls">
                <select
                  id="geofences-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="fences__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>

          {bounded === undefined ? null : (
            <p className="fences__bounded" role="status">
              {bounded}
            </p>
          )}
        </div>

        {/*
        The selected row, for an operator who may not edit. A writer's click opens the form
        instead — one row, two meanings, decided by what the operator may actually do. The panel is
        the `view only` half of a board whose rows were previously inert for exactly those
        operators.
      */}
        {selectedFence === undefined ? null : (
          <RecordDetail
            record={geofenceDetail(selectedFence)}
            open
            onClose={() => setSelectedFenceId(undefined)}
          />
        )}
      </div>

      {/*
        The closing note, in the design's own callout. Its sentence is this screen's rather than the
        design's, because the design has no sentence for a page it does not draw — and the one fact
        an operator needs about a boundary they are about to move is which trips it governs.
      */}
      <div className="fences__note">
        <span aria-hidden="true" className="fences__note-glyph">
          info
        </span>
        <span>
          Evaluations read the boundary in force at the moment they are made, and answer with the
          version they used. Amending a fence does not move a service area that pinned it.
        </span>
      </div>

      {target === undefined ? null : (
        <div className="fences__overlay" onClick={closeForm} role="presentation">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={target.kind === "new" ? "Add a geofence" : `Edit · ${target.fence.name}`}
            className="fences__dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="fences__dialog-head">
              <span aria-hidden="true" className="fences__dialog-glyph">
                polyline
              </span>
              <span className="fences__dialog-title">
                <strong>
                  {target.kind === "new" ? "Add a geofence" : `Edit · ${target.fence.name}`}
                </strong>
                <span className="fences__dialog-sub">
                  {target.kind === "new"
                    ? "Fields marked * are required."
                    : `GEO-05 · version ${target.fence.version}. An amendment carries this version.`}
                </span>
              </span>
              <button
                type="button"
                aria-label="Close"
                className="fences__dialog-close"
                onClick={closeForm}
              >
                close
              </button>
            </div>

            <div className="fences__dialog-body">
              {saved === undefined ? null : (
                <div role="status" className="fences__dialog-done">
                  <span aria-hidden="true" className="fences__dialog-done-glyph">
                    check_circle
                  </span>
                  <span>{saved}</span>
                </div>
              )}
              {refusal === undefined ? null : (
                <div
                  role="alert"
                  className={
                    refusal.kind === "conflict" ? "fences__dialog-conflict" : "fences__dialog-error"
                  }
                >
                  <span aria-hidden="true" className="fences__dialog-error-glyph">
                    {refusal.kind === "conflict" ? "merge_type" : "error"}
                  </span>
                  <span>
                    {refusal.kind === "conflict" ? (
                      <>
                        Somebody changed this first. {refusal.message} Close this and open the row
                        again to amend the boundary now in force.
                      </>
                    ) : refusal.kind === "forbidden" ? (
                      <>
                        You do not have access to this. It needs {WRITE_SCOPE}. Ask whoever
                        administers your roles for that permission.
                      </>
                    ) : refusal.kind === "error" ? (
                      <>
                        {refusal.message} ({refusal.code})
                      </>
                    ) : (
                      "That did not work."
                    )}
                  </span>
                </div>
              )}
              {problems.length === 0 ? null : (
                <div role="alert" className="fences__dialog-error">
                  <span aria-hidden="true" className="fences__dialog-error-glyph">
                    error
                  </span>
                  <span>
                    Complete {problems.length} field{problems.length > 1 ? "s" : ""}:{" "}
                    {problems.map((problem) => problem.field).join(", ")}.
                  </span>
                </div>
              )}

              <fieldset className="fences__fieldset">
                <legend>Fence</legend>
                <div className="fences__field-grid">
                  <label className="fences__field fences__field--wide">
                    <span className="fences__label">
                      Name<span className="fences__required">*</span>
                    </span>
                    <input
                      type="text"
                      value={draft.name}
                      placeholder="e.g. JKIA terminal 1A"
                      aria-invalid={problemFor("name") !== undefined}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, name: event.target.value }))
                      }
                    />
                    {problemFor("name") === undefined ? null : (
                      <span className="fences__hint fences__hint--bad">{problemFor("name")}</span>
                    )}
                  </label>
                  <label className="fences__field">
                    <span className="fences__label">
                      Shape<span className="fences__required">*</span>
                    </span>
                    <select
                      value={draft.shape}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          shape: event.target.value as GeofenceDraft["shape"],
                        }))
                      }
                    >
                      <option value="circle">Circle</option>
                      <option value="polygon">Polygon</option>
                    </select>
                  </label>
                  {/*
                    The status field exists only on an amendment, and that is the contract's shape
                    rather than a simplification. `CreateGeofence` publishes `name` and `geometry`
                    and no status: GEO-05 creates every fence `active`. Offering the choice on the
                    add form would collect a value the request has no field for.
                  */}
                  {target.kind === "edit" ? (
                    <label className="fences__field">
                      <span className="fences__label">
                        State<span className="fences__required">*</span>
                      </span>
                      <select
                        value={draft.status}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            status: event.target.value as GeofenceStatus,
                          }))
                        }
                      >
                        {FILTERABLE_STATUSES.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                      <span className="fences__hint">
                        An inactive fence cannot be evaluated; GEO-05 refuses with a 409.
                      </span>
                    </label>
                  ) : null}
                </div>
              </fieldset>

              <fieldset className="fences__fieldset">
                <legend>Boundary</legend>
                {draft.shape === "circle" ? (
                  <div className="fences__field-grid">
                    <label className="fences__field">
                      <span className="fences__label">
                        Centre latitude<span className="fences__required">*</span>
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draft.latitude}
                        placeholder="-1.31923"
                        aria-invalid={problemFor("latitude") !== undefined}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, latitude: event.target.value }))
                        }
                      />
                      {problemFor("latitude") === undefined ? null : (
                        <span className="fences__hint fences__hint--bad">
                          {problemFor("latitude")}
                        </span>
                      )}
                    </label>
                    <label className="fences__field">
                      <span className="fences__label">
                        Centre longitude<span className="fences__required">*</span>
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draft.longitude}
                        placeholder="36.92782"
                        aria-invalid={problemFor("longitude") !== undefined}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, longitude: event.target.value }))
                        }
                      />
                      {problemFor("longitude") === undefined ? null : (
                        <span className="fences__hint fences__hint--bad">
                          {problemFor("longitude")}
                        </span>
                      )}
                    </label>
                    <label className="fences__field">
                      <span className="fences__label">
                        Radius (m)<span className="fences__required">*</span>
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={draft.radiusMeters}
                        placeholder="1200"
                        aria-invalid={problemFor("radiusMeters") !== undefined}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, radiusMeters: event.target.value }))
                        }
                      />
                      {problemFor("radiusMeters") === undefined ? null : (
                        <span className="fences__hint fences__hint--bad">
                          {problemFor("radiusMeters")}
                        </span>
                      )}
                    </label>
                  </div>
                ) : (
                  <div className="fences__field-grid">
                    <label className="fences__field fences__field--wide">
                      <span className="fences__label">
                        Corners<span className="fences__required">*</span>
                      </span>
                      <textarea
                        rows={6}
                        value={draft.vertices}
                        placeholder={"-1.28333, 36.81667\n-1.28333, 36.83000\n-1.27000, 36.83000"}
                        aria-invalid={problemFor("vertices") !== undefined}
                        onChange={(event) =>
                          setDraft((current) => ({ ...current, vertices: event.target.value }))
                        }
                      />
                      <span
                        className={
                          problemFor("vertices") === undefined
                            ? "fences__hint"
                            : "fences__hint fences__hint--bad"
                        }
                      >
                        {problemFor("vertices") ??
                          "One latitude, longitude per line, at least three. The closing repeat is added for you."}
                      </span>
                    </label>
                  </div>
                )}
              </fieldset>

              {/*
                What this form cannot offer, said rather than left as a missing control.

                The design's own `Boundary` field is a file drop reading "Draw on the map or upload
                GeoJSON", and neither half is buildable here: no design draws a drawing surface for
                this console, and GEO-05's geometry is a `{latitude, longitude}` object union rather
                than GeoJSON — so a GeoJSON upload would need a converter whose coordinate order
                nothing in the contract states. R-19.
              */}
              <div className="fences__dialog-note">
                <span aria-hidden="true" className="fences__dialog-note-glyph">
                  info
                </span>
                <span>
                  GEO-05 takes coordinates as latitude and longitude, in that order. Its geometry is
                  not GeoJSON, so a boundary cannot be pasted from a map export without transposing
                  each pair.
                </span>
              </div>
            </div>

            <div className="fences__dialog-foot">
              <button type="button" className="fences__dialog-cancel" onClick={closeForm}>
                Cancel
              </button>
              <button
                type="button"
                className="fences__dialog-save"
                disabled={saving || viewOnly}
                onClick={() => {
                  void submit();
                }}
              >
                {target.kind === "new" ? "Add geofence" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * An idempotency key for one command.
 *
 * `randomUUID` where the origin is secure and a random hex string where it is not. GEO-05 requires
 * 8 to 128 characters and nothing more, so the fallback satisfies the contract; what it does not do
 * is throw on a console served over plain HTTP, which is what `crypto.randomUUID` does.
 */
function randomKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined") crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * geometry. Excel takes an HTML table, which is what `application/vnd.ms-excel` has always meant in
 * practice, and PDF is the browser's own print dialogue.
 */
function exportRows(rows: readonly Geofence[], format: ExportFormat): void {
  const header = ["Fence", "Shape", "Centre", "State", "Version"];
  const body = rows.map((fence) => [
    fence.name,
    shapeText(fence.geometry),
    centreText(fence.geometry),
    stateOf(fence).label,
    String(fence.version),
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "geofences.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "geofences.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>geofences</title></head><body><h2 style="font-family:sans-serif">geofences</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
